declare const google: any;

import {computed} from "vue";
import {ApiGetters, FormActions, FormGetters} from './../Store/index';
import {
    fetchSelectionInteractive,
    getImagesForVehicles,
    getMatomoNameKey,
    getVehicleIdentificationByKba,
    toggleLoadingAnimation,
    isLoadingAnimationEnabled,
    isFormReadyForApiCall
} from './api';
import {arraysAreEqual, isEmpty, stringToBoolean} from './functions';
import {CarItem, FormState} from './../Store/form/types';

interface FilterResult {
    key: string;
    value: any;
}

interface Filter {
    key: string;
    label: string;
    proposals?: any[];
}

export async function handleDependencyUpdate(name: string, value: any, store: any, fetchNewData = true) {
    if (name === 'vehicleSelected') {
        fetchNewData = false;
    }

    if (name == 'firstRegistration' || name == 'mileage') {
        const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;
        const firstRegistration = filter?.firstRegistration ?? null;
        const mileage = filter?.mileage ?? null;
        if (!firstRegistration || !mileage) {
            fetchNewData = false;
        }
    }

    switch (name) {
        case 'isCar': {
            updateAllowedVehicleTypesFilter(store);
            break;
        }
        case 'useHsnTsn': {
            handleUseHsnTsnChange(store, value);
            break;
        }
        case 'fieldHSN':
        case 'fieldTSN': {
            await handleHsnChange(store, value, name);
            break;
        }
        case 'vehicle': {
            await handleVehicleChange(store, value);
            break;
        }
        case 'vehicleSelected': {
            handleVehicleSelected(store, value);
            break;
        }
        case 'fieldModel': {
            await handleModelChange(store, value, name);
            break;
        }
        case 'firstRegistration': {
            handleFirstRegistrationDate(store, value);
            break;
        }
        default: {
            //statements;
            break;
        }
    }

    if (fetchNewData) {
        await fetchSelectionInteractive(store);
    }
}

export async function updateStoreValueAndFilter(
    store: any,
    props: any,
    value: any,
    isValid: boolean,
    isAutoSelected: boolean = false,
    changeFilter = true,
    hasDefaultValue: boolean = false,
    fetchNewData = true,
    resetInputsBool = false
) {
    if (['isCar', 'useHsnTsn'].includes(props.name)) {
        const currentValue = store.getters[`form/${FormGetters.GET_INPUTS}`][props.name].value;
        if (value !== currentValue) {
            store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
                field: 'availableSteps', key: 'value', value: [1]
            });
            store.dispatch(`form/${FormActions.SET_IS_RESSETING_FORM}`, true);
        }
    }
    if (props.name === 'vehicle' && hasDefaultValue) {
        await handleVehicleChange(store, value);
    }
    // Reset Default Values for Predefined Manufacturer
    if (props.name === 'isCar' && !hasDefaultValue) {
        store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
            field: 'predefinedManufacturerValues',
            key: 'hasValue',
            value: false
        });
        store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
            field: 'predefinedManufacturerValues',
            key: 'manufacturerValue',
            value: {
                brand: '',
                vehicle:'',
                vehicleType: true
            }
        });
    }

    const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;
    const filterName = (props.filterProperty[1] as string).toString();

    // Dispatch Vuex actions to update the store for the input value and validity
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {
        field: props.name,
        key: 'value',
        value: value,
    });
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {
        field: props.name,
        key: 'valid',
        value: isValid,
    });
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {
        field: props.name,
        key: 'isAutoSelected',
        value: isAutoSelected,
    });

    // Update the filter data in the store based on filterName and value
    if (filterName && changeFilter) {
        if (isEmpty(value)) {
            store.dispatch(`form/${FormActions.DELETE_DATA}`, {
                field: 'filter',
                key: 'value',
                value: filterName,
            });
        } else {
            store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
                field: 'filter',
                key: 'value',
                value: {
                    ...filter,
                    [filterName]: value,
                },
            });
        }
    }

    if (resetInputsBool || isEmpty(value)) {
        const inputsAboveOrder = getInputsAboveOrder(store, props.name);
        resetInputs(store, inputsAboveOrder, 'resetValue');
        resetAutoSelectedInputsBasedOnFilter(store, inputsAboveOrder);
    }

    // Handle dependency updates
    await handleDependencyUpdate(props.name, value, store, changeFilter && fetchNewData);
}

export function getCarsBelowThreshold(store: any) {
    let carsBelowThreshold: CarItem[] = [];
    if (isCarCountBelowThreshold(store)) {
        const tmpCarsBelowThreshold = store.getters[`form/${FormGetters.GET_DATA}`].vehicleSelection.value;

        // Check if tmpCarsBelowThreshold is an array
        if (Array.isArray(tmpCarsBelowThreshold)) {
            carsBelowThreshold = tmpCarsBelowThreshold.map(car => {
                return {
                    k: car.ecode,
                    v: car.fullAusstattungslinie && car.fullAusstattungslinie.trim() !== ''
                        ? car.fullAusstattungslinie
                        : car.containerName
                };
            });
        }
    }
    return carsBelowThreshold;
}

export function getPropertyFilter(property: string, store: any): FilterResult {
    const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;
    let result: FilterResult = { key: '', value: undefined };

    if (Object.entries(filter).length) {
        Object.entries(filter).forEach(([key, value]) => {
            if (key === property) {
                result = { key, value };
                return;
            }
        });
    }

    return result;
}

export function getFilterIndex(filterName: string, store: any): number {
    const filters: Filter[] = store.getters[`form/${FormGetters.GET_DATA}`]?.filters?.value || [];

    // Check if filters is an array before using findIndex
    if (!Array.isArray(filters)) {
        return -1; // or handle this case as appropriate for your use case
    }

    const index = filters.findIndex(filter => filter && filter.key === filterName);

    return index !== -1 ? index : 0;
}

export function resetAutoSelectedInputsBasedOnFilter(store: any, excludeFields: string[] = []) {
    const inputs = store.getters[`form/${FormGetters.GET_INPUTS}`];
    const filters = store.getters[`form/${FormGetters.GET_DATA}`].filters.value;
    const isCar = store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value;

    if (Object.keys(filters).length > 0) {
        Object.entries(filters).forEach(([filterKey, filter]) => {
            // Check if the filter has 'proposals' property with length 1
            // @ts-ignore
            if (filter.proposals && filter.proposals.length === 1) {
                // Iterate through inputs and reset auto-selected ones associated with this filter
                Object.entries(inputs).forEach(([key, input]) => {
                    if (!isEmpty(excludeFields) && excludeFields.includes(key)) {
                        return;
                    }

                    // @ts-ignore
                    const currentFilterName = isCar ? input.apiFilterName : input.apiMotorCycleFilterName;

                    if (currentFilterName === 'vehicleSelected') {
                        handleVehicleSelected(store, '')
                    }
                    // @ts-ignore
                    else if (currentFilterName === filter.key) {
                        // Reset the select box
                        store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'value', value: '' });
                        store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'valid', value: false });
                        store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'reset', value: false });
                        store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'isAutoSelected', value: false });

                        store.dispatch(`form/${FormActions.DELETE_DATA}`, {field: 'filters', key: 'value', value: filterKey});
                        return;
                    }
                });
            }
        });
    }
}

export function handleVehicleSelected(store: any, value: any) {
    const cars = getCarsBelowThreshold(store);

    if (isEmpty(value) && cars.length === 1) {
        return;
    }
    else if (isEmpty(value)) {
        store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'selectedCar', key: 'value', value: {} });
        resetVehicleSelected(store, false);
    } else {
        const selectedCar = findObjectByEcode(store.getters[`form/${FormGetters.GET_DATA}`].vehicleSelection.value, value);

        if (selectedCar !== null && Object.keys(selectedCar).length > 0) {
            selectedCar['mainTypeNameWithoutYear'] = selectedCar['mainTypeName'].replace(/\s*\(\s*([0-9]{2}\.)?[0-9]{4}\s*->\s*\)/, '');

            store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: 'vehicleSelected', key: 'value', value: selectedCar });
            store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: 'vehicleSelected', key: 'valid', value: true });
            store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: 'vehicleSelected', key: 'reset', value: false });
            store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'selectedCar', key: 'value', value: selectedCar });

            getImagesForVehicles(store);
        }
    }
}

export async function validation(store: any, timer: number = 2000) {
    const filter = store.getters[`form/${FormGetters.GET_DATA}`].filter.value;
    const firstRegistration = filter?.firstRegistration ?? null;
    const mileage = filter?.mileage ?? null;
    let toggleAnimation = true;
    if (!firstRegistration || !mileage) {
        toggleAnimation = false;
    }
    if (toggleAnimation) {
        await toggleLoadingAnimation(true);
    }
    const inputs: FormState['inputs'] = store.getters[`form/${FormGetters.GET_INPUTS}`],
        filters = store.getters[`form/${FormGetters.GET_DATA}`].filters.value,
        useHsnTsn = store.getters[`form/${FormGetters.GET_INPUTS}`].useHsnTsn.value,
        isCar = store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value,
        selectedCar = store.getters[`form/${FormGetters.GET_INPUTS}`].vehicleSelected.value,
        vehicleType = isCar ? 'car' : 'motorcycle',
        steps = {...store.getters[`form/${FormGetters.GET_STEPS}`]};

    // Initialize tempSteps object
    const tempSteps = {},
        requiredItemsNotMarked = {};

    store.dispatch(`form/${FormActions.SET_IS_VALID}`, true);

    if (Object.keys(inputs).length > 0) {
        Object.entries(inputs).forEach((entry) => {
            const [key, input] = entry;

            if (
                (key === 'vehicle' && useHsnTsn) ||
                ((key === 'fieldHSN' || key === 'fieldTSN') && !useHsnTsn) ||
                (key === 'fieldEmail' && !input.checked)
            ) {
                steps[input.step] = steps[input.step].filter((item) => item !== key);
                return;
            }

            const filterName = isCar ? input.apiFilterName : input.apiMotorCycleFilterName;
            const hasFilter = (input.apiFilterName || input.apiMotorCycleFilterName) ? true : false;
            const filterIndex = getFilterIndex(filterName, store);

            if (
                filters &&
                (isEmpty(filters[filterIndex]) || isEmpty(filters[filterIndex].proposals))
            ) {
                if (isEmpty(selectedCar)) {
                    store.dispatch(`form/${FormActions.SET_IS_VALID}`, false);
                }

                steps[input.step] = steps[input.step].filter((item) => item !== key);
                return;
            }

            if (input.validation && !input.validation.includes(vehicleType)) {
                steps[input.step] = steps[input.step].filter((item) => item !== key);
                return;
            }

            if (hasFilter && !isEmpty(selectedCar)) {
                return;
            }

            // Check if input.step is in steps and key is in the corresponding array
            if (steps[input.step] && steps[input.step].includes(key) && input.valid) {
                let stepInt = input.step;

                // Move the key to tempSteps
                tempSteps[stepInt] = tempSteps[stepInt] || [];
                tempSteps[stepInt].push(key);
            }

            if (!input.valid) {
                store.dispatch(`form/${FormActions.SET_IS_VALID}`, false);
                requiredItemsNotMarked[input.step] = requiredItemsNotMarked[input.step] || [];
                requiredItemsNotMarked[input.step].push(key);
            }
            if (input.value) {
                store.dispatch(`form/${FormActions.SET_IS_TOUCHED}`, true);
                addNewValueToFunnelStep(store, filterName, input.value);
            }
        });
    }

    const vehicleSelected =  {...store.getters[`form/${FormGetters.GET_INPUTS}`].vehicleSelected.value};

    let tmpAvailableSteps = [1];
    if (Object.keys(tempSteps).length > 0) {
        const requiredItemsNotMarkedKeys = Object.keys(requiredItemsNotMarked);

        // Compare tempSteps with the original steps
        if (!isEmpty(vehicleSelected)) {
            tmpAvailableSteps = Object.keys((steps)).map(Number);

            if (requiredItemsNotMarkedKeys.length > 0) {
                tmpAvailableSteps.push(Math.min(...requiredItemsNotMarkedKeys.map(Number)));
            }
        } else {
            let breakLoop = false;
            Object.entries(tempSteps).forEach(([step, fields]) => {
                // Check if arrays are equal
                let stepInt = parseInt(step);
                const stepFields = Array.from(steps[stepInt]); // Convert Proxy to array
                if (arraysAreEqual(fields, stepFields)) {
                    if (!breakLoop) {
                        // Add step to availableSteps field
                        if (!tmpAvailableSteps.includes(stepInt)) {
                            tmpAvailableSteps.push(stepInt);
                        }

                        let nextStep = stepInt + 1;

                        if (!tmpAvailableSteps.includes(nextStep)) {
                            if (requiredItemsNotMarkedKeys.includes(String(nextStep))) {
                                breakLoop = true;
                            }
                            tmpAvailableSteps.push(nextStep);
                        }
                    }
                }
            });
        }

        // Get the car count from the store's data using Vuex getters
        const carCount = store.getters[`form/${FormGetters.GET_DATA}`].carCount.value;
        const tempStepsKeys = Object.keys(tempSteps);

        // Check if the car count is zero
        if (carCount === 0 && parseInt(tempStepsKeys[tempStepsKeys.length - 1]) !== tmpAvailableSteps[tmpAvailableSteps.length - 1]) {
            // If there are no cars, remove the last step from the available steps array
            tmpAvailableSteps.pop();
        }
    }

    tmpAvailableSteps = modifyAvailableSteps(store, tmpAvailableSteps);

    store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
        field: 'availableSteps', key: 'value', value: tmpAvailableSteps
    });

    if (store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`] === 0) {
        setTimeout(async () => {
            // Fetch new data
            if (toggleAnimation) {
                await toggleLoadingAnimation(false);
            }
        }, timer);
    }
}

export function calculateProgressBarData(store: any) {
    const inputs: FormState['inputs'] = store.getters[`form/${FormGetters.GET_INPUTS}`],
        filters = store.getters[`form/${FormGetters.GET_DATA}`].filters.value,
        useHsnTsn = store.getters[`form/${FormGetters.GET_INPUTS}`].useHsnTsn.value,
        isCar = store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value,
        vehicleType = isCar ? 'car' : 'motorcycle';

    let totalInputs = 0;
    let correctInputs = 0;

    if (Object.keys(inputs).length > 0) {
        Object.entries(inputs).forEach((entry) => {
            const [key, input] = entry;

            if (
                (key === 'vehicle' && useHsnTsn) ||
                ((key === 'fieldHSN' || key === 'fieldTSN') && !useHsnTsn) ||
                (key === 'fieldEmail' && !input.checked)
            ) {
                return;
            }

            const filterName = isCar ? input.apiFilterName : input.apiMotorCycleFilterName;
            const filterIndex = getFilterIndex(filterName, store);

            if (
                filters &&
                (isEmpty(filters[filterIndex]) || isEmpty(filters[filterIndex].proposals))
            ) {
                return;
            }

            if (input.validation && !input.validation.includes(vehicleType)) {
                return;
            }

            totalInputs += 1;

            if (input.valid) {
                correctInputs += 1;
            }
        });
    }

    return totalInputs > 0 ? (correctInputs / totalInputs) * 100 : 0;
}

export function addNewValueToFunnelStep(store: any, filterName: string, value: any) {
    const matomoName = getMatomoNameKey(filterName);
    if (matomoName) {
        store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
            key: 'properties',
            value: {
                ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
                ...{[matomoName]: value}
            },
        });
    }
}

export function useShowElementByPredefinedManufacturer(store, name) {
    const isCar = computed(() => store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value);
    const predefinedManufacturer = computed(() => store.getters[`form/${FormGetters.GET_DATA}`].predefinedManufacturerValues);
    const hasValue = computed(() => predefinedManufacturer.value.hasValue);
    const manufacturerValue = computed(() => predefinedManufacturer.value.manufacturerValue);

    const showElement = computed(() => {
        if (!hasValue.value) {
            return true;
        }

        if ((name === "isCar" || name === "useHsnTsn") && !isEmpty(manufacturerValue.value.brand)) {
            return false;
        }

        if ((isCar.value && name === "fieldModel") && !isEmpty(manufacturerValue.value.vehicle)) {
            return false;
        }

        if ((!isCar.value && name === "fieldModelType") && !isEmpty(manufacturerValue.value.vehicle)) {
            return false;
        }

        if (
            (name === "manufacturer" || name === "isCar" || name === "useHsnTsn" || name === "vehicle") &&
            !isEmpty(manufacturerValue.value.brand)
        ) {
            return false;
        }

        if (name === "mileage" && !isEmpty(predefinedManufacturer.value.mileage)) {
            return false;
        }

        if (name === "firstRegistration" && !isEmpty(predefinedManufacturer.value.firstRegistration)) {
            return false;
        }

        return true;
    });

    return showElement;
}

export function callPaq (
    functionName: string,
    category: string = '',
    action: string = '',
    name: string = '',
    value: string = ''
) {
    if (typeof window !== 'undefined' && window['_paq']) {
        switch (functionName) {
            case 'trackEvent': {
                window['_paq'].push([functionName, category, action, name]);
                break;
            }
            case 'trackPageView': {
                window['_paq'].push([functionName]);
                break;
            }
            case 'setCustomUrl': {
                window['_paq'].push([functionName, value]);
                break;
            }
            default: {
                //statements;
                break;
            }
        }
    }
}

export function pushFunnelStepToMatomo(store: any) {
    let properties = store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
        funnelStepExtra = store.getters[`form/${FormGetters.GET_FUNNEL_STEP_EXTRA}`];

    if (Object.keys(funnelStepExtra).length) {
        properties = {...properties, ...funnelStepExtra};
    }

    // @ts-ignore
    window._mtm.push({
        'event': 'funnelStep',
        'funnelStep': {
            'properties': properties
        }
    });
}

export function isStepAvailable(store: any, step: number) {
    const availableSteps = Array.from(store.getters[`form/${FormGetters.GET_DATA}`].availableSteps.value);

    if (store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`] !== 0 || isLoadingAnimationEnabled()) {
        setTimeout(async () => {
            // Fetch new data
            isStepAvailable(store, step);
        }, 250);
    }

    return availableSteps.includes(step);
}

export function scrollIntoStep(store: any, step: number) {
    const availableSteps = Array.from(store.getters[`form/${FormGetters.GET_DATA}`].availableSteps.value);
    const showResultPage = store.getters[`form/${FormGetters.GET_SHOW_RESULT_PAGE}`];

    if (availableSteps.includes(step) && !showResultPage) {
        const stepElement = document.querySelector(`.step-${step}`);

        if (stepElement) {
            // Remove Hidden class
            stepElement.classList.remove('hidden');

            // Calculate the target scroll position with a 100-pixel offset
            const targetScrollPosition = stepElement.getBoundingClientRect().top + window.scrollY - 100;

            if (store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`] === 0 && isLoadingAnimationEnabled() === false) {
                // Smooth scroll to the target position
                window.scrollTo({
                    top: targetScrollPosition,
                    behavior: 'smooth',
                });
            } else {
                setTimeout(async () => {
                    // Fetch new data
                    scrollIntoStep(store, step);
                }, 250);
            }
        }
    }
}

export function modifyAvailableSteps(store: any, availableSteps: any) {
    const vehicleValue = store.getters[`form/${FormGetters.GET_INPUTS}`].vehicle.value,
      useHsnTsn = store.getters[`form/${FormGetters.GET_INPUTS}`].useHsnTsn.value,
      fieldHSN = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldHSN.value,
      fieldTSN = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldTSN.value;

      // Add step 2 if the form is ready and step 2 is not already in the array
    if (
      !isFormReadyForApiCall(store) &&
      (!isEmpty(vehicleValue) || (!isEmpty(useHsnTsn) && !isEmpty(fieldHSN) && !isEmpty(fieldTSN))) &&
      !availableSteps.includes(2)
    ) {
        availableSteps.push(2);
    }

    return availableSteps;
}

export function resetInputs(store: any, excludeFields: string[]  = [], resetValue = '') {
    const inputs = store.getters[`form/${FormGetters.GET_INPUTS}`];

    if (Object.keys(inputs).length > 0) {
        Object.keys(inputs).forEach((key, input) => {
            if (key === 'isCar' || key === 'useHsnTsn') {
                return;
            }

            if (!isEmpty(excludeFields) && excludeFields.includes(key)) {
                return;
            }

            store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'value', value: resetValue });
            store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'valid', value: false });
            store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'reset', value: false });
            store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: key, key: 'isAutoSelected', value: false });

            const filterName = inputs[key].apiFilterName;
            if (filterName) {
                store.dispatch(`form/${FormActions.DELETE_DATA}`, {
                    field: 'filter',
                    key: 'value',
                    value: filterName,
                });
            }
        });
    }
}

export function getInput(store: any, apiFilterName: string) {
    const inputs = store.getters[`form/${FormGetters.GET_INPUTS}`];

    if (Object.keys(inputs).length > 0) {
        // Find the input with the given apiFilterName
        const foundInput = (Object.entries(inputs) as [string, any][])
            .find(([key, input]) => input.apiFilterName === apiFilterName);

        if (foundInput) {
            const [key, input] = foundInput;
            return { key, input };
        }
    }

    // Return null if no match is found
    return null;
}

export async function navigateToStepWithRouterLink(store: any, step: number, routerLink: string) {
    const location = store.getters[`form/${FormGetters.GET_LOCATION}`];
    const oldRouterLink = location.routerLink;

    await store.dispatch(`form/${FormActions.UPDATE_LOCATION}`, {
        key: 'routerLink',
        value: routerLink,
    });

    await store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
        key: 'properties',
        value: {
            ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
            ...{
                stepProcessed: oldRouterLink,
                stepProcessedNum: step === 1 ? 2 : 1,
                stepURL: store.getters[`form/${FormGetters.GET_LOCATION_WITH_HASH}`],
                referrerURL: location.oldUrl,
            },
        },
    });

    await store.dispatch(`form/${FormActions.UPDATE_LOCATION}`, {
        key: 'oldUrl',
        value: store.getters[`form/${FormGetters.GET_LOCATION_WITH_HASH}`],
    });

    updateWindowLocation(store);

    if (routerLink !== 'result') {
        // add funnelStep to Matomo
        pushFunnelStepToMatomo(store);
    }
}

export async function setResettingFormToFalseWhenNoRequests(store: any) {
    if (store.getters[`api/${ApiGetters.GET_ON_GOING_REQUEST}`] === 0) {
        await store.dispatch(`form/${FormActions.SET_IS_RESSETING_FORM}`, false);
    } else {
        setTimeout(() => setResettingFormToFalseWhenNoRequests(store), 100); // Retry after 100ms
    }
}

export function updateWindowLocation(store: any) {
    const location = store.getters[`form/${FormGetters.GET_LOCATION_WITH_HASH}`];

    if (location) {
        window.location.href = location;
    } else {
        console.error('Location not available.');
    }
}

export function getLocationWithoutHash() {
    return window.location.href.replace(/#/, '');
}

export function getInputsAboveOrder(store: any, parameterKey: string): string[] {
    const inputs: FormState['inputs'] = store.getters[`form/${FormGetters.GET_INPUTS}`];
    const parameterOrder = inputs[parameterKey].order;

    // Use Object.keys to get an array of keys from the inputs object
    const inputKeys = Object.keys(inputs);

    // Filter the keys based on the order condition
    return inputKeys.filter(key => inputs[key].order <= parameterOrder);
}

export function triggerMapSearch(zipValue: string) {
  try {
    if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
      console.warn('Google Maps JS API not loaded. Skipping map search.');
      return;
    }
    const btnMap = document.querySelector('.btn-map-submit') as HTMLButtonElement | null;
    const scrollTarget = document.getElementById('sachverstaendige-suche');

    if (!btnMap) {
      console.log('No btnMap found');
      return;
    }

    // Set ZIP into input field
    const mapInput = document.querySelector('#bh-sl-address') as HTMLInputElement | null;
    if (mapInput) {
      mapInput.value = zipValue;
    }

    // Setup MutationObserver to cancel unwanted scroll trigger
    if (scrollTarget) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'scrool') {
            scrollTarget.removeAttribute('scrool');
            // Optional: Restore original scroll position if needed
            window.scrollTo({top: window.scrollY});
          }
        }
      });

      observer.observe(scrollTarget, {attributes: true});
    }

    // Now trigger the button click
    btnMap.click();
  } catch (error) {
    console.error('Error while triggering map search:', error);
  }
}

export function checkGoogleMapsConsent(): Promise<boolean> {
  const hasConsent = window.UC_UI?.getServicesBaseInfo?.().some(
    (service) => service.id === 'S1pcEj_jZX' && service.consent.status === true
  ) ?? false;

  const isScriptInDOM = Array.from(document.getElementsByTagName('script')).some(script => {
    const src = script.src || '';
    return src.includes('maps.googleapis.com') || src.includes('maps.google.com');
  });

  return new Promise((resolve) => {
    if (!hasConsent || !isScriptInDOM) {
      return resolve(false);
    }

    const testImg = new Image();
    testImg.onload = () => resolve(true);
    testImg.onerror = () => resolve(false);

    // This confirms if maps.google.com is reachable and not blocked
    testImg.src = 'https://maps.google.com/favicon.ico';
  });
}

export async function trackUserEmailForValuation(store) {
  const emailField = store.getters[`form/${FormGetters.GET_INPUTS}`].fieldEmail;

  if (emailField.checked && emailField.valid) {
    try {
      const response = await fetch(`/cdpApi.json?action=setEmail&email=${encodeURIComponent(emailField.value)}`);
      if (response.ok) {
        const data = await response.json();
        if (data?.uid) {
          const uid = data.uid;

          // @ts-ignore
          let containsUid = window._mtm?.some((element) => element.eh === uid);
          if (!containsUid) {
            // @ts-ignore
            window._mtm.push({ eh: uid });
          }
        }
      }
    } catch (error) {
      console.warn('Email API call failed:', error);
    }

    await store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP_EXTRA}`, {
      key: 'marketing_consent',
      value: true,
    });
  }
}


function isCarCountBelowThreshold(store: any) {
    const carCount = store.getters[`form/${FormGetters.GET_DATA}`].carCount.value,
        carThresholdCount = store.getters[`form/${FormGetters.GET_DATA}`].carThresholdCount.value;

    return carCount > 0 && (carCount < carThresholdCount);
}

function updateAllowedVehicleTypesFilter(store: any) {
    const isCar = store.getters[`form/${FormGetters.GET_INPUTS}`].isCar.value,
        allowedVehicleTypes = isCar ? ['1', '2'] : ['3'];

    //reset vehicle Selected
    resetVehicleSelected(store);

    // reset filter
    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'filter', key: 'value', value: {}});
    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'filters', key: 'value', value: {}});
    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'carCount', key: 'value', value: 0});
    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'allowedVehicleTypes', key: 'value', value: allowedVehicleTypes});
    store.dispatch(`form/${FormActions.RESET_FUNNEL_STEP}`);
    store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'availableSteps', key: 'value', value: [1]});
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`,{field: 'useHsnTsn', key: 'value', value: false});
    store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
        key: 'properties',
        value: {
            ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
            ...{carType: isCar ? 'Automobil' : 'Motorrad'}
        },
    });

    // Reset inputs
    resetInputs(store);
    resetHsnTsnFields(store);

    // _paq
    callPaq('trackEvent', 'Gebrauchtfahrzeugwerte', 'verwendete Filter', 'allowedVehicleTypes');
}

function handleUseHsnTsnChange(store: any, value: any) {
    resetInputs(store);
    resetVehicleSelected(store);

    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`,{field: 'useHsnTsn', key: 'value', value: stringToBoolean(value)});
    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'filters', key: 'value', value: {}});
    store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'identificationByKba', key: 'value', value: stringToBoolean(value)});
    store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'availableSteps', key: 'value', value: [1]});
    store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
        key: 'properties',
        value: {
            ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
            ...{certificateAvailable: stringToBoolean(value) ? "yes" : "no"}
        },
    });

    if (!value) {
        resetHsnTsnFields(store);
        updateAllowedVehicleTypesFilter(store);
    }
}

async function handleVehicleChange(store: any, value: any) {
    resetInputs(store, ['vehicle', 'fieldHSN', 'fieldTSN']);
    await store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'availableSteps', key: 'value', value: [1]});

    // If a vehicle's brand is changed or deleted, the entire filter must be deleted.
    if (
        isEmpty(value) ||
        value !== store.getters[`form/${FormGetters.GET_DATA}`].filter.value.manufacturer
    ) {
        // reset filter
        resetAutoSelectedInputsBasedOnFilter(store);
        await store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'filter', key: 'value', value: {}});
        await store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'availableSteps', key: 'value', value: [1]});

        // Set First Registration Date if it exists
        if (store.getters[`form/${FormGetters.GET_INPUTS}`].firstRegistration.value) {
            store.dispatch(`form/${FormActions.UPDATE_DATA}`,{field: 'filter', key: 'value', value: {
                    firstRegistration: store.getters[`form/${FormGetters.GET_INPUTS}`].firstRegistration.value
                }
            });
        }
    }
}

async function handleModelChange(store: any, value: any, name: string) {
    // If a vehicle's brand is changed or deleted, the entire filter must be deleted.
    if (isEmpty(value)) {
        resetVehicleSelected(store);

        const availableSteps = store.getters[`form/${FormGetters.GET_DATA}`].availableSteps.value;
        // Check if the array contains 1, 2, and 3
        const containsOneTwoThree = availableSteps.includes(1) && availableSteps.includes(2) && availableSteps.includes(3);
        await store.dispatch(`form/${FormActions.UPDATE_DATA}`,
            {
                field: 'availableSteps',
                key: 'value',
                value: containsOneTwoThree ? [1,2,3] : availableSteps
            }
        );

        const manufacturerProperty: FilterResult = getPropertyFilter('manufacturer', store);

        let newFilter = {[manufacturerProperty.key]: manufacturerProperty.value};

        // Set First Registration Date if it exists
        if (store.getters[`form/${FormGetters.GET_INPUTS}`].firstRegistration.value) {
            newFilter['firstRegistration'] = store.getters[`form/${FormGetters.GET_INPUTS}`].firstRegistration.value;
        }

        if (manufacturerProperty && Object.keys(manufacturerProperty).length !== 0) {
            await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: name, key: 'value', value: value.value});
            await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: name, key: 'valid', value: false});
            await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: 'vehicle', key: 'value', value: manufacturerProperty.value});
            await store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, {field: 'vehicle', key: 'valid', value: true});
            await store.dispatch(`form/${FormActions.UPDATE_DATA}`, {
                field: 'filter',
                key: 'value',
                value: newFilter,
            });
        }
    }
}

function handleFirstRegistrationDate(store: any, value: any) {
    const dateObject = new Date(value);

    if (!isNaN(dateObject.getTime())) {
        // Valid date
        const year = dateObject.getFullYear();
        const month = dateObject.getMonth() + 1; // Adding 1 because months are zero-indexed

        store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
            key: 'properties',
            value: {
                ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
                ...{
                    initialRegistrationYear: year,
                    initialRegistrationMonth: month
                }
            },
        });
    }
}

async function handleHsnChange(store: any, value: any, name: string) {
    function getCorrectName(input: string): string {
        const last3Chars = input.slice(-3); // Get last 3 characters
        return last3Chars.charAt(0).toUpperCase() + last3Chars.slice(1).toLowerCase();
    }

    const functionName = getCorrectName(name);
    const propertyKey = `certificateAvailable${functionName}`;

    store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'hsnTsnService', key: name, value: value});
    store.dispatch(`form/${FormActions.UPDATE_FUNNEL_STEP}`, {
        key: 'properties',
        value: {
            ...store.getters[`form/${FormGetters.GET_FUNNEL_STEP}`].properties,
            ...{[propertyKey]: value}
        },
    });

    if (isEmpty(value)) {
        store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'availableSteps', key: 'value', value: [1]});
    }

    resetInputs(store, ['fieldHSN', 'fieldTSN']);

    await getVehicleIdentificationByKba(store);
}

function findObjectByEcode(vehicles, ecode) {
    if (Array.isArray(vehicles)) {
        return vehicles.find(obj => obj.ecode === ecode);
    } else {
        return null;
    }
}

function resetHsnTsnFields(store: any) {
    store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'hsnTsnService', key: 'fieldHSN', value: '' });
    store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'hsnTsnService', key: 'fieldTSN', value: '' });
    store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'hsnTsnService', key: 'datECodes', value: '' });
    store.dispatch(`form/${FormActions.UPDATE_DATA}`, { field: 'datECodes', key: 'datECodes', value: [] });
}

function resetVehicleSelected(store: any, isCallApi: boolean = true) {
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: 'vehicleSelected', key: 'value', value: {}});
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: 'vehicleSelected', key: 'valid', value: false });
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: 'vehicleSelected', key: 'reset', value: false });
    store.dispatch(`form/${FormActions.UPDATE_INPUTS}`, { field: 'vehicleSelected', key: 'isAutoSelected', value: false });

    if (isCallApi) {
        store.dispatch(`form/${FormActions.UPDATE_DATA}`, {field: 'vehicleSelection', key: 'value', value: {}});
    }
}
